import {
  Button,
  DatePicker,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  VirtualizedCombobox,
  VirtualizedMultiSelectCombobox,
} from '@roger/ui-components';
import { X } from 'lucide-react';
import { useEffect } from 'react';
import { formatDateToISO, parseISOToLocalDate } from '../../utils/dateUtils';
import {
  addFilter,
  defaultValueFor,
  removeFilter,
  updateFilter,
} from '../../reporting/definitionUpdates';
import {
  NO_RELATED,
  refLabel,
  resolveColumnMeta,
  splitRef,
  type RelatedManifests,
} from '../../reporting/columnRefs';
import {
  formatValue,
  resolveFormatKind,
  type FormatKind,
} from '../../reporting/formatValue';
import { optionGroups, renderOptionGroups } from './columnOptions';
import { useColumnValues } from '../../reporting/useColumnValues';
import type {
  ColumnType,
  DatasetManifestEntry,
  FilterOp,
  ReportDefinition,
  ReportFilter,
} from '../../types/reporting';

// A UI-only Select value for the forward relative_date direction. Both "in the
// last" and "in the next" persist op 'relative_date' (the wire op never changes);
// the two directions are only distinguishable by the value shape
// ({ last_days } vs { next_days }). Because a Radix Select needs distinct item
// values, the forward entry carries this sentinel, mapped back to op
// 'relative_date' + a next_days value in onOpChange. It is never stored.
const RELATIVE_NEXT_TOKEN = 'relative_date_next';
type OpToken = FilterOp | typeof RELATIVE_NEXT_TOKEN;

const OPS_BY_TYPE: Record<ColumnType, { value: OpToken; label: string }[]> = {
  string: [
    { value: 'contains', label: 'contains' },
    { value: 'eq', label: 'is' },
    { value: 'neq', label: 'is not' },
    { value: 'in', label: 'is one of' },
    { value: 'is_null', label: 'is empty' },
    { value: 'not_null', label: 'is not empty' },
  ],
  number: [
    { value: 'eq', label: 'is' },
    { value: 'gt', label: 'more than' },
    { value: 'gte', label: 'at least' },
    { value: 'lt', label: 'less than' },
    { value: 'lte', label: 'at most' },
    { value: 'between', label: 'between' },
    { value: 'is_null', label: 'is empty' },
    { value: 'not_null', label: 'is not empty' },
  ],
  date: [
    { value: 'relative_date', label: 'in the last' },
    { value: RELATIVE_NEXT_TOKEN, label: 'in the next' },
    { value: 'between', label: 'between' },
    { value: 'is_null', label: 'is empty' },
    { value: 'not_null', label: 'is not empty' },
  ],
  boolean: [{ value: 'eq', label: 'is' }],
};

const RELATIVE_PRESETS = [7, 30, 90, 365];
const DEFAULT_RELATIVE_DAYS = 30;
// Mirrors the backend MAX_RELATIVE_DAYS ceiling. A legacy row could carry a
// last_days far beyond any real window (e.g. 1e100); the compiler would feed it
// to a JS Date whose arithmetic overflows and throws when the report runs. Cap it
// here so such a value is treated as unusable and healed to the default window.
const MAX_RELATIVE_DAYS = 36600;

// A saved/legacy filter value is untrusted JSONB replayed in the builder, so the
// value inputs must read it defensively rather than blind-casting to the shape
// the operator implies.
//
// A relative_date value is directional: { last_days } looks backward,
// { next_days } forward, exactly one key. The intended direction — used to heal a
// broken value within its own direction — is read from which key is present, even
// if that key's count is unusable. A value with neither key defaults to 'last'.
const relativeDirection = (value: unknown): 'last' | 'next' =>
  value != null && typeof value === 'object' && 'next_days' in value
    ? 'next'
    : 'last';

// The usable day-count of a relative_date value, or null. Mirrors compileReport's
// relativeDateWindow acceptance test so the editor and the compiler agree on
// which values are usable (a garbage value — null, a string, an object without a
// finite positive last_days/next_days — is not usable and is repaired to the
// default window). The ceiling keeps the window inside JS Date's arithmetic range.
const usableRelativeDays = (value: unknown): number | null => {
  if (value == null || typeof value !== 'object') return null;
  const key =
    'next_days' in value
      ? 'next_days'
      : 'last_days' in value
        ? 'last_days'
        : null;
  if (key === null) return null;
  const days = (value as Record<string, unknown>)[key];
  return typeof days === 'number' &&
    Number.isFinite(days) &&
    days >= 1 &&
    days <= MAX_RELATIVE_DAYS
    ? days
    : null;
};

const hasUsableRelativeDays = (value: unknown): boolean =>
  usableRelativeDays(value) !== null;

// The value shape for a relative_date window in the given direction.
const relativeValueFor = (
  direction: 'last' | 'next',
  days: number
): { last_days: number } | { next_days: number } =>
  direction === 'next' ? { next_days: days } : { last_days: days };

// A persisted filter element is untrusted: a legacy row written by the old,
// weakly-validated endpoint can be null, a non-object, or missing its column/op.
// Reading such an element (f.op, f.column) throws during render, so narrow every
// element to a renderable ReportFilter before touching it — anything that fails
// the shape check is skipped rather than crashing the builder.
const isRenderableFilter = (f: unknown): f is ReportFilter =>
  f != null &&
  typeof f === 'object' &&
  typeof (f as { column?: unknown }).column === 'string' &&
  typeof (f as { op?: unknown }).op === 'string';

// An operator that isn't offered for the column's type (a legacy contains-on-date
// row, or an unknown "bogus" op) has no coherent value input and compiles to SQL
// DuckDB rejects. Snap it to the column type's default operator for display so the
// row renders sensibly instead of a blank operator over a mismatched input; the
// first edit then persists the normalized op.
const normalizeOp = (op: string, type: ColumnType): FilterOp => {
  const ops = OPS_BY_TYPE[type];
  // Only a real wire op is ever stored (never the RELATIVE_NEXT_TOKEN sentinel),
  // and ops[0] is a real FilterOp for every type, so the cast is safe.
  return ops.some(o => o.value === op)
    ? (op as FilterOp)
    : (ops[0].value as FilterOp);
};

const relativeDaysValue = (value: unknown): number =>
  usableRelativeDays(value) ?? DEFAULT_RELATIVE_DAYS;

// The Select value for a filter's operator. A relative_date carrying a next_days
// value shows the forward sentinel ("in the next"); everything else shows its
// wire op directly.
const opToken = (filter: ReportFilter): OpToken =>
  filter.op === 'relative_date' && relativeDirection(filter.value) === 'next'
    ? RELATIVE_NEXT_TOKEN
    : filter.op;

// A between value must be a [lo, hi] pair; any other shape (a scalar, an object,
// null) would throw when array-destructured, so normalize it to empty bounds.
const betweenBounds = (value: unknown): [unknown, unknown] => {
  if (Array.isArray(value)) return [value[0], value[1]];
  return ['', ''];
};

// Mirrors compileReport's isValidBound date test: a bound is only a usable date
// when it is a real ISO calendar date. A date-shaped-but-impossible string
// (legacy JSONB '2026-02-30') is rejected — DuckDB can't cast it and the
// compiler drops the whole filter. parseISOToLocalDate would silently roll it to
// a valid day (03/02/2026), making the DatePicker look filled while the filter
// runs inactive; this check keeps the editor honest so such a bound reads as
// empty/incomplete instead. Uses the numeric Date constructor (no string
// parsing): day 0 of month+1 is the last valid day of `month`.
const isValidIsoDate = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return false;
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12) return false;
  const lastDayOfMonth = new Date(Number(match[1]), month, 0).getDate();
  return day >= 1 && day <= lastDayOfMonth;
};

// "is"/"is not" on a string column: let the user pick from the column's actual
// values (type-to-filter) instead of guessing free text. Split into its own
// component so the values hook obeys the rules of hooks (never called inside the
// conditional valueInputs branch).
const StringValueCombobox = ({
  valuesDataset,
  column,
  formatKind,
  filter,
  index,
  onSelect,
}: {
  valuesDataset: DatasetManifestEntry;
  column: string;
  formatKind: FormatKind;
  filter: ReportFilter;
  index: number;
  onSelect: (index: number, value: string) => void;
}) => {
  const { values, loading, capped } = useColumnValues(
    valuesDataset,
    column,
    true
  );
  const current = filter.value == null ? '' : String(filter.value);
  // A saved (or out-of-cap) value may not be in the distinct list, in which case
  // the combobox would show its "Select value" placeholder while the report is
  // still actively filtered on it. Seed the current value into the options so the
  // trigger always reflects what's applied.
  const optionValues =
    current && !values.includes(current) ? [current, ...values] : values;
  // Label each option through the SAME formatter the result cells, CSV/Excel
  // export, and chart use, so a value reads identically wherever it appears
  // ("covid_19" → "COVID-19", "mrsa" → "MRSA", "injury_except_major" → "Injury
  // except major"). Only the label is formatted: `value` stays raw, so what is
  // stored and matched is unchanged. emptyText is the raw value because a
  // distinct-value option is never the null placeholder.
  const options = optionValues.map(value => ({
    value,
    label: formatValue(value, formatKind, { emptyText: value }),
  }));
  // Size the scrollable list to its content (one 35px virtual row each, capped at
  // the default 300px) so a handful of values don't sit in a mostly-empty popover.
  const listHeight = `${Math.min(300, Math.max(1, options.length) * 35)}px`;
  return (
    <VirtualizedCombobox
      size='sm'
      height={listHeight}
      className='min-w-0 flex-1'
      // The trigger is narrow in the filters sidebar; without an explicit width
      // the popover inherits that width and truncates several values to the same
      // prefix ("OASIS E…"), making them impossible to tell apart. Give the
      // dropdown room to show full labels while staying within the viewport.
      popoverWidth='min(24rem, 90vw)'
      options={options}
      value={current}
      onValueChange={value => onSelect(index, value)}
      isLoading={loading}
      placeholder='Select value'
      searchPlaceholder={capped ? 'Type to filter (top 200)' : 'Type to filter'}
    />
  );
};

const stringValueOptions = (
  values: string[],
  currentValues: string[],
  formatKind: FormatKind
) => {
  const mergedValues = [
    ...currentValues.filter(value => value !== ''),
    ...values,
  ];
  return [...new Set(mergedValues)].map(value => ({
    value,
    label: formatValue(value, formatKind, { emptyText: value }),
  }));
};

const StringMultiValueCombobox = ({
  valuesDataset,
  column,
  formatKind,
  filter,
  index,
  onSelect,
}: {
  valuesDataset: DatasetManifestEntry;
  column: string;
  formatKind: FormatKind;
  filter: ReportFilter;
  index: number;
  onSelect: (index: number, value: string[]) => void;
}) => {
  const { values, loading, capped } = useColumnValues(
    valuesDataset,
    column,
    true
  );
  const currentValues = Array.isArray(filter.value)
    ? filter.value
        .map(value => (value == null ? '' : String(value)))
        .filter(value => value.trim() !== '')
    : [];
  const options = stringValueOptions(values, currentValues, formatKind);
  const listHeight = `${Math.min(300, Math.max(1, options.length) * 35)}px`;
  return (
    <VirtualizedMultiSelectCombobox
      height={listHeight}
      className='min-w-0 flex-1'
      buttonClassName='h-7 min-w-0 px-2 py-1 text-xs'
      popoverWidth='min(24rem, 90vw)'
      options={options}
      value={currentValues}
      onValueChange={value => onSelect(index, value)}
      openOnMount={currentValues.length === 0}
      placeholder={loading ? 'Loading values' : 'Select values'}
      searchPlaceholder={capped ? 'Type to filter (top 200)' : 'Type to filter'}
    />
  );
};

interface FiltersEditorProps {
  dataset: DatasetManifestEntry;
  definition: ReportDefinition;
  onChange: (definition: ReportDefinition) => void;
  related?: RelatedManifests;
}

const FiltersEditor = ({
  dataset,
  definition,
  onChange,
  related = NO_RELATED,
}: FiltersEditorProps) => {
  // A legacy/garbage relative_date value (null, a string, an object without a
  // usable last_days) is dropped by compileReport, so the report runs unfiltered
  // — yet the editor's defensive read shows the default 30-day window. That
  // display/execution mismatch misleads the user ("last 30 days" but the filter
  // is not applied). Repair such a value in the definition itself to the same
  // default the editor shows, so the compiler actually applies the window and
  // display and execution agree. New saves can't persist a malformed
  // relative_date (the API rejects it); this only heals already-stored rows.
  useEffect(() => {
    // isRenderableFilter guards the `.op`/`.value` reads: a malformed legacy
    // element (null, a non-object) must not throw here, and it isn't a
    // relative_date to repair anyway.
    const needsRepair = definition.filters.some(
      f =>
        isRenderableFilter(f) &&
        f.op === 'relative_date' &&
        !hasUsableRelativeDays(f.value)
    );
    if (!needsRepair) return;
    onChange({
      ...definition,
      filters: definition.filters.map(f =>
        isRenderableFilter(f) &&
        f.op === 'relative_date' &&
        !hasUsableRelativeDays(f.value)
          ? {
              // Heal within the value's own direction: a broken "in the next"
              // value heals to a next_days default, not a backward window.
              ...f,
              value: relativeValueFor(
                relativeDirection(f.value),
                DEFAULT_RELATIVE_DAYS
              ),
            }
          : f
      ),
    });
  }, [definition, onChange]);

  const columnType = (ref: string): ColumnType =>
    resolveColumnMeta(ref, dataset, related)?.type ?? 'string';

  const setFilter = (index: number, filter: ReportFilter) =>
    onChange(updateFilter(definition, index, filter));

  const valueInputs = (filter: ReportFilter, index: number) => {
    const type = columnType(filter.column);
    if (filter.op === 'is_null' || filter.op === 'not_null') return null;
    if (filter.op === 'relative_date') {
      const days = relativeDaysValue(filter.value);
      const direction = relativeDirection(filter.value);
      return (
        <Select
          value={String(days)}
          onValueChange={value =>
            setFilter(index, {
              ...filter,
              value: relativeValueFor(direction, Number(value)),
            })
          }
        >
          <SelectTrigger selectSize='sm' className='w-28'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RELATIVE_PRESETS.map(days => (
              <SelectItem key={days} value={String(days)}>
                {days} days
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    if (filter.op === 'between') {
      const [lo, hi] = betweenBounds(filter.value);
      const isDate = type === 'date';
      // Mirror the compiler's isValidBound so the hint tracks whether the filter
      // will actually apply: a date bound must be a real ISO calendar date (an
      // impossible '2026-02-30' is dropped by the compiler even though it looks
      // filled), a number bound a finite number. Any bound that fails leaves the
      // between inactive, so treat it as incomplete and show the hint rather than
      // letting two valid-looking pickers hide a silently-disabled filter.
      const boundUsable = (bound: unknown): boolean =>
        isDate
          ? isValidIsoDate(bound)
          : typeof bound === 'number' && Number.isFinite(bound);
      const incomplete = !boundUsable(lo) || !boundUsable(hi);
      // Both bounds are individually usable but the range is inverted (From
      // after To — dates 05/31 → 05/01, numbers 100 → 10). isFilterActive still
      // treats this as active and the compiler emits BETWEEN hi AND lo, which
      // matches no rows: a valid-looking pair that silently returns nothing.
      // Flag it so the user fixes the order rather than assuming the data is
      // empty. ISO 'yyyy-MM-dd' strings sort chronologically, so a lexicographic
      // compare is a correct date compare (bounds are already validated usable).
      const reversed =
        !incomplete &&
        (isDate
          ? String(lo).trim() > String(hi).trim()
          : (lo as number) > (hi as number));
      // Keep a cleared number input as '' (not Number('') === 0) so an empty
      // bound reads as incomplete and the filter stays inactive until filled.
      const parseNumber = (raw: string) => (raw === '' ? '' : Number(raw));
      const setBounds = (nextLo: unknown, nextHi: unknown) =>
        setFilter(index, { ...filter, value: [nextLo, nextHi] });
      // Date bounds persist as ISO 'yyyy-MM-dd' strings (what the compiler casts
      // to DATE); convert to/from a Date only at the DatePicker boundary so the
      // stored value shape is unchanged. A cleared picker maps back to '' so the
      // bound reads as incomplete and the filter stays inactive.
      const boundRow = (
        boundLabel: string,
        bound: unknown,
        onBound: (value: unknown) => void
      ) => (
        <div className='flex items-center gap-1'>
          <span className='w-8 shrink-0 text-xs text-muted-foreground'>
            {boundLabel}
          </span>
          {isDate ? (
            <DatePicker
              datePickerSize='sm'
              // Override the picker's default fixed 280px width so the trigger
              // shrinks to the narrow filters sidebar instead of overflowing it.
              className='w-full min-w-0 flex-1'
              // Only feed a real ISO calendar date to the picker. An
              // impossible '2026-02-30' would be rolled to a valid day by
              // parseISOToLocalDate, showing a filled picker for a bound the
              // compiler discards; leave it empty so display matches execution.
              // Trim first: the compiler's isValidBound trims before validating,
              // so a padded ' 2026-05-01 ' is an *active* filter — but
              // parseISOToLocalDate's strict regex rejects the untrimmed string
              // and would blank the picker for a filter that actually runs.
              date={
                isValidIsoDate(bound)
                  ? parseISOToLocalDate(bound.trim())
                  : undefined
              }
              onDateChange={date => onBound(date ? formatDateToISO(date) : '')}
            />
          ) : (
            <Input
              inputSize='sm'
              type='number'
              className='min-w-0 flex-1'
              value={String(bound ?? '')}
              onChange={e => onBound(parseNumber(e.target.value))}
            />
          )}
        </div>
      );
      // Stack the two bounds as From/To rows so the pair fits the narrow
      // filters sidebar instead of overflowing it with two side-by-side inputs.
      return (
        <div className='flex flex-1 flex-col gap-1'>
          {boundRow('From', lo, value => setBounds(value, hi))}
          {boundRow('To', hi, value => setBounds(lo, value))}
          {incomplete ? (
            <p className='text-xs text-muted-foreground'>
              Add both {isDate ? 'dates' : 'values'} to apply
            </p>
          ) : (
            reversed && (
              <p className='text-xs text-muted-foreground'>
                From can&rsquo;t be after To
              </p>
            )
          )}
        </div>
      );
    }
    if (
      type === 'string' &&
      (filter.op === 'eq' || filter.op === 'neq' || filter.op === 'in')
    ) {
      // Distinct values for a related column come from the related view directly
      // (no join needed). If the related entry is unavailable, the primary
      // dataset + full dotted ref is passed — useColumnValues sees an unknown
      // column and stays idle, which is the safe degrade.
      const { dataset: refDatasetKey, column: refColumn } = splitRef(
        filter.column
      );
      const valuesDataset =
        (refDatasetKey ? related.get(refDatasetKey) : dataset) ?? dataset;
      const valuesColumn =
        refDatasetKey && related.get(refDatasetKey) ? refColumn : filter.column;
      // resolveFormatKind reads the column's manifest entry, and treats a column
      // it can't find as a numeric measure alias (which would pass values
      // through raw). This branch only runs for string-typed columns and for
      // unknown columns that columnType() defaulted to 'string', so degrade a
      // column with no manifest entry to 'text' — the generic enum humanizer.
      const valuesMeta = resolveColumnMeta(filter.column, dataset, related);
      const formatKind: FormatKind = valuesMeta
        ? resolveFormatKind(valuesColumn, new Map([[valuesColumn, valuesMeta]]))
        : 'text';
      if (filter.op === 'in') {
        return (
          <StringMultiValueCombobox
            valuesDataset={valuesDataset}
            column={valuesColumn}
            formatKind={formatKind}
            filter={filter}
            index={index}
            onSelect={(i, value) => setFilter(i, { ...filter, value })}
          />
        );
      }
      return (
        <StringValueCombobox
          valuesDataset={valuesDataset}
          column={valuesColumn}
          formatKind={formatKind}
          filter={filter}
          index={index}
          onSelect={(i, value) => setFilter(i, { ...filter, value })}
        />
      );
    }
    if (type === 'boolean') {
      return (
        <Select
          value={String(filter.value)}
          onValueChange={value =>
            setFilter(index, { ...filter, value: value === 'true' })
          }
        >
          <SelectTrigger selectSize='sm' className='w-24'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='true'>Yes</SelectItem>
            <SelectItem value='false'>No</SelectItem>
          </SelectContent>
        </Select>
      );
    }
    return (
      <Input
        inputSize='sm'
        type={type === 'number' ? 'number' : 'text'}
        value={String(filter.value ?? '')}
        onChange={e =>
          setFilter(index, {
            ...filter,
            value: type === 'number' ? Number(e.target.value) : e.target.value,
          })
        }
      />
    );
  };

  return (
    <div className='space-y-2'>
      {definition.filters.map((rawFilter, index) => {
        // Skip a malformed legacy element instead of throwing on its `.column`
        // read; its position in the array is preserved so setFilter/removeFilter
        // still target the right index.
        if (!isRenderableFilter(rawFilter)) return null;
        const type = columnType(rawFilter.column);
        // Normalize an operator the column type doesn't support to a coherent
        // default, so the row (op select + value input) renders sensibly and the
        // first edit persists a valid op rather than the garbage one.
        const filter: ReportFilter = {
          ...rawFilter,
          op: normalizeOp(rawFilter.op, type),
        };
        const columnLabel = refLabel(filter.column, dataset, related);
        return (
          <div key={index} className='space-y-1 rounded-md border p-2'>
            {/* Field select gets its own full-width row so long column labels
                ("Submitted date") aren't truncated in the narrow sidebar. */}
            <div className='flex items-center gap-1'>
              <Select
                value={filter.column}
                onValueChange={value => {
                  const nextType = columnType(value);
                  // ops[0] is a real FilterOp for every column type (never the
                  // relative_date_next sentinel, which is offered second).
                  const op = OPS_BY_TYPE[nextType][0].value as FilterOp;
                  setFilter(index, {
                    column: value,
                    op,
                    value: defaultValueFor(op, nextType),
                  });
                }}
              >
                <SelectTrigger
                  selectSize='sm'
                  className='flex-1'
                  title={columnLabel}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {renderOptionGroups(optionGroups(dataset, related))}
                </SelectContent>
              </Select>
              <Button
                size='sm'
                variant='ghost'
                aria-label={`Remove ${columnLabel} filter`}
                onClick={() => onChange(removeFilter(definition, index))}
              >
                <X className='h-4 w-4' />
              </Button>
            </div>
            <div className='flex items-center gap-1'>
              <Select
                value={opToken(filter)}
                onValueChange={value => {
                  const token = value as OpToken;
                  // Both relative_date directions persist op 'relative_date';
                  // the sentinel only picks the value key (last_days vs
                  // next_days), preserving the current day-count across a swap.
                  if (
                    token === 'relative_date' ||
                    token === RELATIVE_NEXT_TOKEN
                  ) {
                    const direction =
                      token === RELATIVE_NEXT_TOKEN ? 'next' : 'last';
                    setFilter(index, {
                      ...filter,
                      op: 'relative_date',
                      value: relativeValueFor(
                        direction,
                        relativeDaysValue(filter.value)
                      ),
                    });
                    return;
                  }
                  const op = token;
                  setFilter(index, {
                    ...filter,
                    op,
                    value: defaultValueFor(op, type),
                  });
                }}
              >
                {/* Wide enough for the longest plain-language operator
                    ("is not empty") so labels like "more than" don't truncate to
                    "more…" (ambiguous vs "at most"). */}
                <SelectTrigger selectSize='sm' className='w-40'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {OPS_BY_TYPE[type].map(op => (
                    <SelectItem key={op.value} value={op.value}>
                      {op.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Wide value editors get their own full-width row below so the
                  filter sidebar stays readable instead of squeezing labels into
                  a tiny inline control. */}
              {filter.op !== 'between' &&
                filter.op !== 'in' &&
                valueInputs(filter, index)}
            </div>
            {(filter.op === 'between' || filter.op === 'in') &&
              valueInputs(filter, index)}
          </div>
        );
      })}
      <Select
        value=''
        onValueChange={value =>
          onChange(addFilter(definition, value, dataset, related))
        }
      >
        <SelectTrigger selectSize='sm'>
          <SelectValue placeholder='+ Add filter' />
        </SelectTrigger>
        <SelectContent>
          {renderOptionGroups(optionGroups(dataset, related))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default FiltersEditor;
